import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UseFieldArrayAppend, UseFormReturn } from 'react-hook-form';
import { CreateSellerRequestValues } from './types';
import { useLazyGetFactorBuyersQuery } from '@/app/api';
import { useLazyGetDataByTinQuery, useLazyGetDocsQuery } from '@/entities/didox/api';
import { Buyer } from '@/app/api/types';
import { useAppSelector } from '@/app/hooks';
import _ from 'lodash';
import { getStorageItem, setStorageItem } from '@/app/helpers';
import useDebounce from '@/app/hooks/useDebounce';

interface Props {
  openLoader: () => void;
  closeLoader: () => void;
  form: UseFormReturn<CreateSellerRequestValues, unknown, undefined>;
  setBuyersOptions: (buyers: Buyer[]) => void;
  append: UseFieldArrayAppend<CreateSellerRequestValues, 'documents'>;
}

const useCreateSellerRequestFormSideEffects = ({
  openLoader,
  closeLoader,
  form,
  setBuyersOptions,
  append,
}: Props) => {
  const { profile } = useAppSelector((state) => state.auth);
  const [sp] = useSearchParams();
  const { setValue } = form;

  const [getBuyers] = useLazyGetFactorBuyersQuery();
  const [getDocs] = useLazyGetDocsQuery();
  const [getDataByTin] = useLazyGetDataByTinQuery();

  const sellerTin = form.watch('sellerTin');

  const debouncedSellerTin = useDebounce(sellerTin, 500);

  useEffect(() => {
    handleCopyFrmUrl();
  }, [sp]);

  useEffect(() => {
    const getSellerData = async () => {
      if (debouncedSellerTin?.length !== 9) {
        return form.setValue('sellerName', '');
      }

      const res = await getDataByTin({ tin: debouncedSellerTin }, true).unwrap();

      form.setValue('sellerName', res.name);
    };

    getSellerData();
  }, [debouncedSellerTin]);

  const handleCopyFrmUrl = async () => {
    try {
      openLoader();

      const spData = {
        totalWithVat: sp.get('totalWithVat'),
        facturas: sp.get('facturas'),
        buyerTin: sp.get('buyerTin'),
        buyerName: sp.get('buyerName'),
        contractNumber: sp.get('contractNumber'),
        debtSum: sp.get('debtSum'),
        timestamp: sp.get('timestamp'),
        contractDate: sp.get('contractDate'),
      };

      const { totalWithVat, debtSum, contractNumber, buyerName, buyerTin, facturas } = spData;

      const cachedSpData = getStorageItem<typeof spData>('finmakon:request-form:sp');

      if (!cachedSpData.success) {
        setStorageItem('finmakon:request-form:sp', spData, { ttl: 15 * 60 * 1000 });

        window.gtag('event', 'start_invoice_sale', {
          invoice_ids: facturas?.split(',') || [],
          invoice_count: facturas?.split(',').length || 0,
          user_tin: profile?.tin,
          contract_number: contractNumber,
          source: 'didox.uz',
        });
      }

      if (totalWithVat) {
        setValue('contractAmount', totalWithVat);
      }

      if (debtSum) {
        setValue('buyerDebtAmount', debtSum);
      }

      const promises = [];

      // if (contractNumber && contractDate) {
      //   promises.push(
      //     getDocs({
      //       owner: 1,
      //       name: contractNumber,
      //       doctype: '007',
      //     })
      //       .unwrap()
      //       .then((res) => {
      //         const doc =
      //           res.data.find(
      //             (el) => el.partnerTin === buyerTin && el.contract_date === contractDate
      //           ) || res.data?.[0];
      //         setValue('documents.0.value', doc || null);
      //         return res.data;
      //       })
      //   );
      // }

      if (buyerName && buyerTin) {
        promises.push(
          getBuyers({}, true)
            .unwrap()
            .then((res) => {
              const copyRes = _.cloneDeep(res.data);
              if (!res.data.find((el) => el.tin === buyerTin)) {
                copyRes.unshift({
                  name: buyerName,
                  tin: buyerTin,
                });
              }
              setBuyersOptions(copyRes);
              setValue('buyer', {
                name: buyerName,
                tin: buyerTin,
              });
              return copyRes;
            })
        );
      }

      if (facturas) {
        const arr = facturas.split(',');
        promises.push(
          getDocs({
            factoringDocIds: arr,
            owner: 1,
            factoringPartyTin: profile?.tin || '',
          })
            .unwrap()
            .then((res) => {
              setValue('documents.1.value', res.data?.[0] || null);
              res.data.slice(1).forEach((doc) => {
                append({
                  value: doc,
                  type: '002',
                  additionTypes: ['008'],
                  required: true,
                });
              });
              return res.data;
            })
        );
      }

      await Promise.all(promises);
    } finally {
      closeLoader();
    }
  };
};

export default useCreateSellerRequestFormSideEffects;
